import omit from 'lodash-es/omit'
import moment from 'moment-timezone'
import TXT from '@/messages'
import { META_LIFETIME_TICKETS } from '@/config/ticketure-metadata.config'
import { isTruthy } from '@/utils/transformers.utils'
import { LIFETIME_MEMBERSHIP } from '@/config/membership.config'
import { TIME_FORMATS } from '@/config/dates.config'

type ExtractedTiersCategoriesAndTimeslotsT = {
  categories: Transformed.TicketCategoryWithTiers[],
  timeslots: Transformed.Timeslot[],
}

type CategoryAndTimeslotDataT = {
  categories: Transformed.TicketCategory[],
  someOnSale: boolean
  hasLifetimeTickets: boolean
  numTimeslots: number
  allTimeslotsSoldOut: boolean
}

export const isInstanceOfTicketCategoryWithTiers = (
  category: Transformed.TicketCategoryWithTiers | Transformed.TicketCategory,
): category is Transformed.TicketCategoryWithTiers => Boolean(
  category !== null && category !== undefined
  && 'tiers' in category,
)

/**
 * Converts a {@link TicketCategoryWithTiers} to a {@link TicketCategory}
 * @param {TicketCategoryWithTiers} category
 * @returns {TicketCategory} - The converted ticket category
 */
export const convertToTicketCategory = (
  category: Transformed.TicketCategory | Transformed.TicketCategoryWithTiers,
): Transformed.TicketCategory => (isInstanceOfTicketCategoryWithTiers(category)
  ? ({ ...omit(category, 'tiers') })
  : category)

/**
 * Given a current state, returns the desired button text for cart transactions
 * @param isEditing {boolean} - has the user made changes to their order?
 * @param isRemovingAllItems {boolean} - is the cart being cleared?
 * @param lineItemsToDeleteTotal {number} - number of items being removed
 * @param localLineItemsTotal {number} - number of items being added
 * @param cartExists {boolean} - is there an existing cart?
 */
export const getButtonText = (
  isEditing: boolean,
  isRemovingAllItems: boolean,
  lineItemsToDeleteTotal: number,
  localLineItemsTotal: number,
  cartExists: boolean,
): string => {
  if (isEditing && isRemovingAllItems) {
    return TXT.buttons.delete
  }

  if (isEditing && (localLineItemsTotal > 0 || lineItemsToDeleteTotal > 0)) {
    return TXT.buttons.updateOrder
  }

  if (localLineItemsTotal > 0 || !cartExists) {
    return TXT.buttons.addToOrder
  }

  return TXT.buttons.continue
}

/**
 * Given a set of {@link LineItem}s, gets the total quantity of tickets for the `currentEventId`
 * @param currentLineItems {LineItem[][]} - an array of {@link LineItem} arrays
 * @param lineItemsToDelete {LineItem[]} - an array of `LineItems` to be deleted
 * @param categoriesToExclude {string[]} - an array of {@link TicketCategory} ids to exclude when filtering line items
 * @param currentEventId {string} - the {@link Event} id corresponding to the event of interest
 */
export const getNumberOfTicketsFromLineItems = (
  currentLineItems: Transformed.LineItem[][],
  lineItemsToDelete: Transformed.LineItem[],
  categoriesToExclude: string[],
  currentEventId: string,
): number => (
  currentLineItems
    .flat()
    .filter((item) => !categoriesToExclude.includes(item.category.id))
    .filter((item) => item.event.id === currentEventId)
    .reduce((total, current) => total + current.quantity, 0)
  - lineItemsToDelete
    .filter((item) => !categoriesToExclude.includes(item.category.id))
    .filter((item) => item.event.id === currentEventId)
    .reduce((total, current) => total + current.quantity, 0)
)

/**
 * unique ticket categories, tiers, and timeslots from the provided data.
 * @param {EventTicketsAndAddonsWithTimeslots[] | undefined} data
 * - The data containing event tickets, addons, and timeslots.
 * @returns {ExtractedTiersCategoriesAndTimeslotsT} - The extracted categories, tiers, and timeslots.
 */
export const extractTiersCategoriesAndTimeslots = (
  data: readonly Transformed.EventTicketsAndAddonsWithTimeslots[] | undefined,
): ExtractedTiersCategoriesAndTimeslotsT => (data || [])
  .reduce((accumulator: ExtractedTiersCategoriesAndTimeslotsT, currentItem) => {
    // Extracting the tickets array from the current item
    const tickets = currentItem.tickets || []
    const addons = currentItem.addons || []
    accumulator.timeslots.push(currentItem.timeslot)

    const extractData = (
      ticketCategories: readonly Transformed.TicketCategoryWithTiers[],
      acc: ExtractedTiersCategoriesAndTimeslotsT,
    ): void => {
      ticketCategories.forEach((category) => {
        // Checking if the category id is not already in the accumulator
        if (category && !acc.categories.some((el) => el.id === category?.id)) {
          // Adding the unique category title to the accumulator
          acc.categories.push(category)
        }
      })
    }

    // Iterating through each ticket to get its category title
    extractData(tickets, accumulator)
    extractData(addons, accumulator)

    return accumulator
  }, { categories: [], timeslots: [] })

/**
 * Checks if any ticket tier title contains a specified keyword (case-insensitive).
 * @param {TicketTier[]} tiers - Array of ticket tiers to search through.
 * @param {string} title - Keyword to search for in the ticket tier titles.
 * @returns {boolean} - True if any ticket tier title contains the keyword, otherwise false.
 */
export const hasTierTitleKeyword = (
  tiers: Transformed.TicketTier[],
  title: string,
): boolean => {
  /**
   * Helper function to perform case-insensitive comparison for title.
   * @param {string} str - Title of the ticket tier to compare.
   * @returns {boolean} - True if tier title contains the keyword, otherwise false.
   */
  const includesKeyword = (str: string): boolean => str.toLowerCase().includes(title.toLowerCase())

  return tiers.some((el) => includesKeyword(el.title))
}

/**
 * Filter tickets by user membership
 * - Lifetime: if lifetime ticket group (name or meta match) and user is not a lifetime member, filter out
 * @param {TicketCategoryWithTiers[]} tickets - The tickets to filter.
 * @param {Nullable<Transformed.MembershipDetails>} membershipDetails - The user's membership details.
 * @returns {TicketCategoryWithTiers[]} - The filtered tickets.
 */
export const filterTicketsByMembership = (
  tickets: readonly Transformed.TicketCategoryWithTiers[],
  membershipDetails: Nullable<Transformed.MembershipDetails>,
): readonly Transformed.TicketCategoryWithTiers[] => (
  tickets.filter((category) => {
    const isMemberTicketGroup = category?.visibility === 'MEMBER'

    // if not a member ticket group, return true
    if (!isMemberTicketGroup) return true

    const hasLifetimeMeta = category?.meta?.some(
      (meta) => meta?.metakey === META_LIFETIME_TICKETS
        && isTruthy(meta?.value),
    ) || false

    const isLifetimeTicket = category?.title.toLowerCase().includes(LIFETIME_MEMBERSHIP.toLowerCase())
      || hasLifetimeMeta

    return isLifetimeTicket
      ? membershipDetails?.isLifetimePass
      : true
  })
)

const defaultCategoryAndTimeslotData: CategoryAndTimeslotDataT = {
  categories: [],
  someOnSale: false,
  hasLifetimeTickets: false,
  numTimeslots: 0,
  allTimeslotsSoldOut: false,
}

/**
 * Extracts category and timeslot data from the provided ticket and addons data.
 * @param {EventTicketsAndAddonsWithTimeslots[] | undefined} ticketAndAddonsData
 * @returns {CategoryAndTimeslotDataT} - The extracted category and timeslot data.
 */
export const getCategoryAndTimeslotData = (
  ticketAndAddonsData: readonly Transformed.EventTicketsAndAddonsWithTimeslots[] | undefined,
): CategoryAndTimeslotDataT => {
  if (!ticketAndAddonsData) {
    return defaultCategoryAndTimeslotData
  }

  const {
    categories,
    timeslots,
  } = extractTiersCategoriesAndTimeslots(ticketAndAddonsData)

  // We don't show the footer if no ticketCategories are currently on sale
  // salesWindow will be empty if there are no sales window restrictions
  const someOnSale = categories.some(
    (cat) => (
      !cat.salesWindow.length || cat.salesWindow.some((el) => el?.active)
    ),
  )
  // do we have lifetime ticket groups?
  const hasLifetimeTickets = categories.some((category) => (
    category.visibility === 'MEMBER'
    && category.title.toLowerCase().includes(LIFETIME_MEMBERSHIP.toLowerCase())
  ))
  // how many timeslots do we have?
  const numTimeslots = timeslots.length
  // are all timeslots sold out
  const allTimeslotsSoldOut = timeslots
    .map((ts) => ts.soldOut)
    .every((soldOut) => soldOut)

  return {
    categories: categories.map(convertToTicketCategory),
    someOnSale,
    hasLifetimeTickets,
    numTimeslots,
    allTimeslotsSoldOut,
  }
}

/**
 * Updates the current URL by adding or updating the date query parameter.
 * @param {string} dateValue - The new date value to set in the URL query.
 * @param searchParams
 * @returns {string}
 */
export const addDateToQuery = (dateValue: string, searchParams: URLSearchParams): string => {
  const url = new URL(window.location.href)
  const newParams = new URLSearchParams(searchParams.toString())
  newParams.set('date', dateValue)
  url.search = newParams.toString()
  return url.toString()
}

/**
 * Checks if a ticket category has active sales windows
 * For elapsed sales windows, return false
 * For day-of-sale tickets, check active status
 * For non-day-of-sale tickets, return true (upcoming windows show an on-sale message)
 * @param {ReadonlyArray<Transformed.TicketCategoryWithTiers>} tickets - The tickets to check.
 * @returns {boolean} - True if any ticket category has an active sales window, false
 */
const hasActiveSalesWindow = (tickets: readonly Transformed.TicketCategoryWithTiers[]): boolean => (
  tickets.some((ticket) => (
    ticket?.salesWindow.some((salesWindow) => {
      if (salesWindow?.elapsed) return false
      return salesWindow && salesWindow.isDOS ? salesWindow.active : true
    })))
)

/**
 * Checks if a timeslot has not ended yet
 * @param {Timeslot} timeslot - The timeslot to check.
 * @param {string} timeZone - The timezone to use for checking the timeslot end
 * @param {moment.Moment} now - The current time in the specified timezone.
 * @returns {boolean} - True if the timeslot has not ended, false otherwise.
 */
const isTimeslotNotEnded = (
  timeslot: Transformed.Timeslot,
  timeZone: string,
  now: moment.Moment,
): boolean => {
  const endTime = moment(timeslot.endTime)
    .tz(timeZone)
  return endTime.isAfter(now)
}

/**
 * Gets the ids of timeslots that have not ended, so ended sessions are not requested
 * @param {ReadonlyArray<Transformed.Timeslot> | undefined} timeslots - The event's timeslots.
 * @param {string} timeZone - The timezone to use for checking the timeslot end
 * @returns {string[]} - The ids of timeslots that have not ended.
 */
export const getNotEndedTimeslotIds = (
  timeslots: readonly Transformed.Timeslot[] | undefined,
  timeZone: string,
): string[] => {
  const now = moment().tz(timeZone)
  return (timeslots || [])
    .filter((timeslot) => !!timeslot.id && isTimeslotNotEnded(timeslot, timeZone, now))
    .map((timeslot) => timeslot.id)
}

/**
 * Filter day-of-sale tickets by active sales window
 * For day-of-sale tickets, check active status
 * For non-day-of-sale tickets, return true
 * THEN Filter timeslots by end-time
 * The api will not return tickets for elapsed events (after a certain point
 * and for upcoming sales windows, non-day-of-sale,
 * we want to return them as we show them in the UI with a message about when they will be on sale
 *
 * @param {EventTicketsAndAddonsWithTimeslots[]} ticketsAndTimeslots - The tickets to filter.
 * @param {string} timeZone - The timezone to use for filtering tickets.
 * @returns {EventTicketsAndAddonsWithTimeslots[]} - The active tickets.
 */
export const filterActiveTickets = (
  ticketsAndTimeslots: Transformed.EventTicketsAndAddonsWithTimeslots[],
  timeZone: string,
): Transformed.EventTicketsAndAddonsWithTimeslots[] => {
  const now = moment().tz(timeZone)
  return ticketsAndTimeslots
    // filter by sales window for day-of-sale tickets
    .filter((aTicketsAndTimeslots) => hasActiveSalesWindow(aTicketsAndTimeslots.tickets))
    // filter by timeslot end-time for all tickets
    .filter((aTicketsAndTimeslots) => isTimeslotNotEnded(aTicketsAndTimeslots.timeslot, timeZone, now))
}

/**
 * Validates an incoming date against a specific format and checks if it's
 * a valid future or present date for recurring events.
 *
 * @param {string} incomingDate - The date string to validate.
 * @param {boolean} isRecurringEvent - A flag indicating if the event is recurring.
 * @param {string} timezone - The timezone to use for validation.
 * @returns {boolean} - True if the date is valid, false otherwise.
 */
export function isValidCalendarDate(
  incomingDate: Nullable<string>,
  isRecurringEvent: boolean,
  timezone: string,
): boolean {
  const incomingDateMoment = moment.tz(incomingDate, timezone)

  return incomingDateMoment.isValid()
         && isRecurringEvent
         && incomingDateMoment.isSameOrAfter(moment().tz(timezone), 'day')
}

/**
 * Creates a URL query string from a given date string and time zone.
 *
 * @param {string | undefined} date - The date string to convert.
 * @param {string} timeZone - The time zone to use for formatting the date.
 * @returns {string} - The URL query string.
 */
export const createDateString = (date: string | undefined, timeZone: string): string => {
  if (!date) return ''

  const momentDate = moment(date).tz(timeZone)
  const params = new URLSearchParams({
    date: momentDate.format(TIME_FORMATS.calendarDate),
    time: momentDate.format(TIME_FORMATS.hoursAndMinutes),
  })

  return `?${params.toString()}`
}

/**
 * Filters out ticket categories that do not have any tiers available.
 * @param {ReadonlyArray<Transformed.TicketCategoryWithTiers>} categories - The array of ticket categories to filter.
 * @return {ReadonlyArray<Transformed.TicketCategoryWithTiers>} - The filtered array containing only categories with
 * available tiers.
 */
export const filterByAvailableTiers = (
  categories: readonly Transformed.TicketCategoryWithTiers[],
): readonly Transformed.TicketCategoryWithTiers[] => (
  categories.filter((category) => (
    !!category?.tiers?.length && category.capacity.total !== 0
  ))
)

/**
 * Creates a list of active tickets and addons for an event, filtering by membership details and time zone.
 * @param {Transformed.EventTicketsAndAddonsWithTimeslots[] | undefined} ticketsAndAddonsData
 * - The tickets and addons data for the event.
 * @param {Nullable<Transformed.MembershipDetails>} membershipDetails - The user's membership details.
 * @param {string} timeZone - The time zone to use for filtering tickets.
 */
export const createActiveTicketsList = (
  ticketsAndAddonsData: readonly Transformed.EventTicketsAndAddonsWithTimeslots[] | undefined,
  membershipDetails: Nullable<Transformed.MembershipDetails>,
  timeZone: string,
): Transformed.EventTicketsAndAddonsWithTimeslots[] => {
  if (!ticketsAndAddonsData) {
    return []
  }

  // filter lifetime pass tickets
  const ticketsFilteredByMembership = ticketsAndAddonsData
    .map((session) => ({
      ...session,
      tickets: filterTicketsByMembership(session.tickets, membershipDetails),
      addons: filterTicketsByMembership(session.addons, membershipDetails),
    }))

  // TODO: refactor all these filters
  // https://app.asana.com/1/23646394350278/project/1206566497440920/task/1211096484895491?focus=true
  return filterActiveTickets(ticketsFilteredByMembership, timeZone)
    .filter((session) => (
      session.tickets.length > 0
      && filterByAvailableTiers(session.tickets).length > 0))
}

/**
 * Determines if the given incoming date is available for any of the active tickets.
 *
 * @param {Nullable<string>} incomingDate - The incoming date string to check for availability.
 * @param {EventTicketsAndAddonsWithTimeslots[]} activeTickets - The list of active tickets
 * with associated timeslot data.
 * @param {string} timeZone - The timezone to use for date comparison.
 * @returns {boolean} - true if the incoming date matches any active ticket's timeslot start date, false otherwise.
 */
export const isIncomingDateAvailable = (
  incomingDate: Nullable<string>,
  activeTickets: Transformed.EventTicketsAndAddonsWithTimeslots[],
  timeZone: string,
): boolean => {
  if (!incomingDate) return false

  const incomingDateMoment = moment.tz(incomingDate, timeZone)
  return activeTickets.some((day) => (
    moment(day.timeslot.startTime)
      .tz(timeZone)
      .isSame(incomingDateMoment, 'day')
  ))
}
